import {
  DocumentReference,
  ISODateType,
  ISoftDelete,
  Timestamp,
} from '@principle-theorem/shared';
import { IAppointment } from '../appointment/appointment';
import { IBrand } from '../brand';
import { IClinicalNote } from '../clinical-charting/core/clinical-note';
import { IPractice } from '../practice/practice';
import { IStaffer } from '../staffer/staffer';

/** How a scribe session was captured. Persisted — keep the string values stable. */
export enum ScribeSessionSource {
  Live = 'live',
  Upload = 'upload',
}

/** A scribe session's lifecycle state. Persisted — keep the string values stable. */
export enum ScribeSessionStatus {
  InProgress = 'inProgress',
  Ready = 'ready',
  Failed = 'failed',
}

/**
 * The note template a scribe session is generated against. Persisted — keep
 * the string values stable.
 *
 * Both the note type and the note format are tenant-configured on VoxDenta's
 * side (per Damien's email, Sep 2026): the minted token carries the tenant
 * domain, and VoxDenta maps that domain to the tenant's assigned template. The
 * value here is an internal label only — it is not passed to VoxDenta directly
 * and does not influence the vendor's template selection.
 */
export enum ScribeNoteType {
  Dentist = 'dentist',
}

/**
 * Where a scribe session's background note recovery stands. Persisted — keep
 * the string values stable.
 */
export enum ScribeNoteRecoveryStatus {
  Pending = 'pending',
  Recovered = 'recovered',
  Exhausted = 'exhausted',
}

/**
 * The recovery sweep's bookkeeping for a session whose note is fetched from
 * VoxDenta after the client stopped waiting for it.
 */
export interface IScribeNoteRecovery {
  /**
   * `pending` until the sweep adds the note (`recovered`) or stops trying
   * (`exhausted`).
   */
  status: ScribeNoteRecoveryStatus;
  /** Fetches the sweep has made so far. */
  attempts: number;
  /**
   * Earliest instant the sweep may try again. A sweep run also pushes it out
   * while it works on the session, so an overlapping run skips the session.
   */
  nextAttemptAt: Timestamp;
  /**
   * Outcome of the latest step (`requested`, `notReady`, `gaveUp`, ...), kept
   * for support diagnosis.
   */
  lastResult?: string;
}

/**
 * A single AI Scribe recording/upload session for a staffer. Holds the session
 * lifecycle state; the generated note(s) live in the `scribeSessionNotes`
 * subcollection.
 */
export interface IScribeSession extends ISoftDelete {
  stafferRef: DocumentReference<IStaffer>;
  practiceRef: DocumentReference<IPractice>;
  brandRef: DocumentReference<IBrand>;
  appointmentRef?: DocumentReference<IAppointment>;
  source: ScribeSessionSource;
  status: ScribeSessionStatus;
  /**
   * The chart day this session belongs to, keyed identically to
   * `IClinicalNote.recordDate` so a session and the note it is inserted into
   * share one day key. Browser-local, never practice-timezone derived.
   */
  recordDate: ISODateType;
  lastActiveAt?: Timestamp;
  durationSeconds?: number;
  noteType: ScribeNoteType;
  /**
   * The `{authenticatedStafferId}/{sessionId}` path the VoxDenta token is
   * scoped to; differs from `stafferRef` under Recording As. Needed to fetch
   * the note after the client gave up.
   */
  voxSessionPath?: string;
  /**
   * Client instant `stop()` was called; a fetched note older than this is an
   * interim note.
   */
  stoppedAt?: Timestamp;
  /**
   * Set when the client stopped waiting with no note; the
   * `cron-aiScribeNoteRecovery` sweep fetches the note from VoxDenta.
   */
  noteRecovery?: IScribeNoteRecovery;
  /**
   * Count of this session's notes where `deleted === false`.
   * Function-maintained; clients must never write these.
   */
  noteCount?: number;
  /**
   * Count of this session's undeleted notes that have `insertedInto` set.
   * Function-maintained; clients must never write these.
   */
  insertedNoteCount?: number;
  /**
   * `noteCount − insertedNoteCount`: undeleted notes not yet inserted into a
   * clinical note. Function-maintained; clients must never write these.
   */
  pendingNoteCount?: number;
}

/**
 * A note produced within a scribe session. `insertedInto` records the clinical
 * note it was copied into, once the staffer commits it to the patient chart.
 */
export interface IScribeNote extends ISoftDelete {
  content: string;
  voxNoteId?: string;
  name?: string;
  insertedInto?: DocumentReference<IClinicalNote>;
}

/**
 * The single active-session lease for a staffer, stored at
 * `<stafferRef>/scribeLease/<SCRIBE_LEASE_DOC_ID>`. Guarantees one concurrent
 * live scribe session per staffer: a stale lease (no heartbeat within the stale
 * window) lets the next session load take over and fail the abandoned one.
 */
export interface IScribeLease {
  sessionRef: DocumentReference<IScribeSession>;
  lastActiveAt: Timestamp;
}

/**
 * The well-known id of the one lease doc in a staffer's `scribeLease`
 * subcollection. A fixed id is what makes the lease claimable in a transaction:
 * a session must read and write a single known path, never query for it.
 */
export const SCRIBE_LEASE_DOC_ID = 'active';

export enum ScribeSessionCollection {
  Notes = 'scribeSessionNotes',
}
